import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import CourseCard from "../CoursesDisplay/CourseCard";

// Card slot geometry — matches Tailwind classes `w-[260px] lg:w-[300px]` + `gap-4` (16px).
// Keeping these as constants lets the virtualizer compute scroll offsets without DOM measuring.
const SLOT_MOBILE = 260 + 16;
const SLOT_DESKTOP = 300 + 16;
const OVERSCAN = 3; // cards to render beyond each edge of the viewport

const useSlotWidth = () => {
  const [slot, setSlot] = useState(() =>
    typeof window !== "undefined" &&
    window.matchMedia("(min-width: 1024px)").matches
      ? SLOT_DESKTOP
      : SLOT_MOBILE
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = () => setSlot(mq.matches ? SLOT_DESKTOP : SLOT_MOBILE);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return slot;
};

const Shelf = ({
  title,
  icon,
  items = [],
  progressMap = null,
  onSeeAll,
  lazyPaint = true,
}) => {
  const { t, i18n } = useTranslation();
  const sectionRef = useRef(null);
  const scrollRef = useRef(null);
  const rafRef = useRef(0);
  const lastStartRef = useRef(0);
  const arrowDebounceRef = useRef(0);
  const isRTL = i18n.language === "ar";
  const slotWidth = useSlotWidth();

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isMounted, setIsMounted] = useState(!lazyPaint);
  const [visible, setVisible] = useState({ start: 0, end: OVERSCAN * 2 });

  // Pre-mount the shelf well before it enters the viewport. Combined with
  // content-visibility: auto, this gives us "rendered in React, skipped by
  // the browser" — so fast vertical scroll doesn't trigger React mounts on
  // the scroll hot path; the browser just lifts the paint-skip flag.
  useEffect(() => {
    if (!lazyPaint || isMounted) return;
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsMounted(true);
          observer.disconnect();
        }
      },
      { rootMargin: "1500px 0px", threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [lazyPaint, isMounted]);

  // Virtualizer update — bails early unless the render window actually moved
  // past a slot boundary. During fine-grained scroll this means ~1 React
  // update per card-width instead of ~1 per frame.
  const updateVirtualizer = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, clientWidth } = el;
    const pos = Math.abs(scrollLeft);
    const startRaw = Math.floor(pos / slotWidth) - OVERSCAN;
    const span = Math.ceil(clientWidth / slotWidth) + OVERSCAN * 2;
    const start = Math.max(0, startRaw);
    const end = Math.min(items.length, start + span);
    if (lastStartRef.current === start) return; // scrolled within current window
    lastStartRef.current = start;
    setVisible({ start, end });
  }, [items.length, slotWidth]);

  // Arrow-button update — scroll position affects it, but users don't notice
  // the 120ms debounce and it removes an entire render cycle from the scroll
  // hot path.
  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const overflows = scrollWidth - clientWidth > 2;
    let left = false;
    let right = false;
    if (overflows) {
      if (isRTL) {
        left = Math.abs(scrollLeft) + clientWidth < scrollWidth - 2;
        right = scrollLeft < -2;
      } else {
        left = scrollLeft > 2;
        right = scrollLeft + clientWidth < scrollWidth - 2;
      }
    }
    setCanScrollLeft((prev) => (prev === left ? prev : left));
    setCanScrollRight((prev) => (prev === right ? prev : right));
  }, [isRTL]);

  const onScroll = useCallback(() => {
    // Virtualizer on rAF (cheap bail-out), arrows on trailing debounce.
    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        updateVirtualizer();
      });
    }
    clearTimeout(arrowDebounceRef.current);
    arrowDebounceRef.current = setTimeout(updateArrows, 120);
  }, [updateVirtualizer, updateArrows]);

  const recompute = useCallback(() => {
    updateVirtualizer();
    updateArrows();
  }, [updateVirtualizer, updateArrows]);

  useEffect(() => {
    if (!isMounted) return;
    // Settle first-paint measurement.
    const t = setTimeout(recompute, 50);
    return () => clearTimeout(t);
  }, [isMounted, recompute]);

  useEffect(() => {
    if (!isMounted) return;
    window.addEventListener("resize", recompute);
    return () => {
      window.removeEventListener("resize", recompute);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (arrowDebounceRef.current) clearTimeout(arrowDebounceRef.current);
    };
  }, [isMounted, recompute]);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const amount = scrollRef.current.offsetWidth * 0.8;
    const delta = direction === "next" ? amount : -amount;
    scrollRef.current.scrollBy({
      left: isRTL ? -delta : delta,
      behavior: "smooth",
    });
  };

  const visibleItems = useMemo(
    () => items.slice(visible.start, visible.end),
    [items, visible.start, visible.end]
  );
  const leadWidth = visible.start * slotWidth;
  const tailCount = Math.max(0, items.length - visible.end);
  const tailWidth = tailCount * slotWidth;

  if (!items || items.length === 0) return null;

  const hasOverflow = canScrollLeft || canScrollRight;

  return (
    <section
      ref={sectionRef}
      className="mt-10 lg:mt-14"
      // We previously used content-visibility:auto here. It's the right tool
      // for pages with hundreds of shelves, but at fast scroll velocities it
      // causes a 1-frame "empty → content" flash as the browser catches up
      // with paint. We already limit work via (a) React isMounted gate
      // 1500px ahead and (b) horizontal virtualization — so the browser can
      // safely paint the whole mounted tree without that flash.
      style={!isMounted ? { minHeight: "380px" } : undefined}
    >
      {!isMounted ? (
        <div className="space-y-4">
          <div className="h-7 w-48 bg-white/5 rounded-md" />
          <div className="flex gap-4 overflow-hidden">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="shrink-0 w-[260px] lg:w-[300px] h-[260px] lg:h-[340px] rounded-xl bg-white/[0.03]"
              />
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              {icon}
              <h2 className="text-xl lg:text-2xl font-bold text-white">
                {title}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              {onSeeAll && (
                <button
                  onClick={onSeeAll}
                  className="text-sm text-primary hover:text-primary/80 transition-colors font-medium"
                >
                  {t("general.see_all") || "See all"} →
                </button>
              )}
              {hasOverflow && (
                <div className="hidden md:flex items-center gap-1">
                  <button
                    onClick={() => scroll("prev")}
                    disabled={!canScrollLeft}
                    className={`w-8 h-8 rounded-full border flex items-center justify-center transition-colors ${
                      canScrollLeft
                        ? "bg-white/5 hover:bg-white/15 border-white/10 text-white"
                        : "bg-white/[0.02] border-white/5 text-white/30 cursor-not-allowed"
                    }`}
                    aria-label="Previous"
                  >
                    {isRTL ? <CaretRight size={14} /> : <CaretLeft size={14} />}
                  </button>
                  <button
                    onClick={() => scroll("next")}
                    disabled={!canScrollRight}
                    className={`w-8 h-8 rounded-full border flex items-center justify-center transition-colors ${
                      canScrollRight
                        ? "bg-white/5 hover:bg-white/15 border-white/10 text-white"
                        : "bg-white/[0.02] border-white/5 text-white/30 cursor-not-allowed"
                    }`}
                    aria-label="Next"
                  >
                    {isRTL ? <CaretLeft size={14} /> : <CaretRight size={14} />}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Horizontal rail — virtualized via lead/tail spacers so only
              visible (+ overscan) cards are mounted in the DOM. We used to
              force a GPU layer here with willChange+translateZ; on a page
              with many rails, Chrome's layer budget would drop and re-paint
              those layers when they left/entered viewport, showing up as
              "content prints again" on scroll-back. Default compositing is
              more stable. */}
          <div
            ref={scrollRef}
            onScroll={onScroll}
            className="flex gap-4 overflow-x-auto scrollbar-hide"
            style={{
              scrollbarWidth: "none",
              overscrollBehaviorX: "contain",
            }}
          >
            {leadWidth > 0 && (
              <div
                aria-hidden="true"
                className="shrink-0"
                style={{ width: leadWidth - 16 /* gap */ }}
              />
            )}
            {visibleItems.map((item, i) => {
              const idx = visible.start + i;
              const course = item?.course || item;
              const courseId = course?._id;
              const progress = progressMap?.[courseId] || null;
              return (
                <div
                  key={courseId || idx}
                  className="shrink-0 w-[260px] lg:w-[300px]"
                >
                  <CourseCard course={course} progress={progress} />
                </div>
              );
            })}
            {tailWidth > 0 && (
              <div
                aria-hidden="true"
                className="shrink-0"
                style={{ width: tailWidth - 16 /* gap */ }}
              />
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default Shelf;
